import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";
import {
  FaFolder,
  FaStar,
  FaBuilding,
  FaShieldAlt,
  FaHandshake,
  FaLightbulb,
  FaAward,
  FaTasks,
  FaUsers,
  FaBullseye,
  FaRocket,
  FaEye,
  FaCogs,
  FaServer,
  FaCode,
  FaBullhorn,
} from "react-icons/fa";
import { motion } from "framer-motion";
import CountUp from "react-countup";

// Stats for Impactful Numbers section
const stats = [
  {
    id: 1,
    icon: (
      <FaShieldAlt className="w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 text-blue-200" />
    ),
    number: 130,
    suffix: "+",
    title: "Tech Experts & Security Specialists",
    delay: 0,
  },
  {
    id: 2,
    icon: (
      <FaAward className="w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 text-blue-200" />
    ),
    number: 45,
    suffix: "+",
    title: "Industry Certifications & Recognitions",
    delay: 0.2,
  },
  {
    id: 3,
    icon: (
      <FaBuilding className="w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 text-blue-200" />
    ),
    number: 2,
    suffix: "",
    title: "Our Offices",
    delay: 0.4,
  },
];

const AboutUs = ({ name }) => {
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const whyItems = [
    {
      side: "right",
      title: "Skilled & Passionate Professionals",
      desc: "Our team combines strong technical knowledge with creativity and a problem-solving mindset, helping businesses stay ahead in a constantly evolving digital world.",
    },
    {
      side: "left",
      title: "Strong Industry Knowledge",
      desc: "With experience across multiple industries, we understand real business challenges and deliver practical, effective and high-impact solutions.",
    },
    {
      side: "right",
      title: "Reliable Support & Service",
      desc: "We’re approachable, dependable, and always ready to assist, ensuring your operations run smoothly without interruption.",
    },
    {
      side: "left",
      title: "Results That Matter",
      desc: "We aim for meaningful outcomes, not just completed tasks, ensuring every solution creates measurable value for your business.",
    },
    {
      side: "right",
      title: "Client-Centric Approach",
      desc: "We take the time to understand your goals and tailor our services to match them, so every solution fits your needs perfectly.",
    },
    {
      side: "left",
      title: "All-in-One Digital Partner",
      desc: "From managing IT infrastructure and building custom software to handling branding and digital marketing, we provide end-to-end support for your complete digital journey.",
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [name]);

  useEffect(() => {
    AOS.init({ duration: 700, once: true, easing: "ease-out" });
  }, []);

  // Scroll Progress & Back-to-Top Visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / scrollHeight) * 307.919;
      setScrollProgress(progress);

      setShowScroll(scrollTop > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>
          About SecurXpert Technologies | IT Solutions & Digital Services
        </title>
        <meta
          name="description"
          content="Learn about SecurXpert Technologies - trusted IT solutions, software development, and digital marketing services. Discover our mission, values, and commitment to secure digital transformation."
        />
        <meta
          name="keywords"
          content="SecurXpert Technologies, IT solutions, software development, digital marketing, cybersecurity, cloud services, digital transformation"
        />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <header className="relative w-full">
        <div
          className="
            relative w-full h-[317px]
            bg-no-repeat bg-cover
            bg-[center_10%]
          "
          style={{ backgroundImage: "url(/img/whoweare.png)" }}
        >
          {/* Centered text content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 drop-shadow-md">
              Who We Are
            </h1>

            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-[2px]"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Who We Are</span>
            </div>
          </div>
        </div>
      </header>

      {/* WELCOME SECTION */}
      <section className="w-full px-4 sm:px-8 md:px-14 lg:px-14 py-8 sm:py-12 md:py-16 lg:py-19 bg-white">
        <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 md:gap-10 lg:gap-11 justify-center items-center w-full">
          <div className="flex flex-col gap-4 sm:gap-5 md:gap-5.5 lg:gap-5.5 justify-start items-start w-full lg:w-1/2">
            <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[40px] font-bold leading-tight text-[#210a4a]">
              Welcome to
              <br />
              SecurXpert Technologies
            </h2>
            <p className="text-sm sm:text-base font-normal leading-6 text-[#5f5d5d] w-full">
              We are a trusted tech partner, providing IT solutions, custom software development and digital marketing services that enable businesses to grow with secure, scalable and innovative digital technologies. Our team delivers tailored solutions that streamline operations and enhance customer experience. From startups to enterprises, we help brands build strong digital presence and achieve long-term success
            </p>
          </div>

          <div className="flex justify-center items-center w-full lg:w-auto">
            <div className="border border-[#210a4a] rounded-r-[50px] sm:rounded-r-[75px] md:rounded-r-[100px] p-2 sm:p-3 md:p-3.5">
              <img
                src="/img/whoheroi.jpg"
                className="w-full max-w-[270px] sm:max-w-[400px] md:max-w-[480px] lg:w-[538px] h-auto rounded-r-[50px] sm:rounded-r-[75px] md:rounded-r-[100px] object-cover"
                alt="SecurXpert Technologies team"
              />
            </div>
          </div>
        </div>

        {/* Purpose, Mission, Vision, What We Do */}
        <div className="flex flex-col lg:flex-row justify-center items-center w-full gap-6 sm:gap-8 md:gap-10 lg:gap-11 mt-12 sm:mt-16 md:mt-18 lg:mt-20.5">
          <div className="flex justify-center items-center w-full lg:w-1/2">
            <div className="flex justify-start items-start w-full relative max-w-[500px] mx-auto">
              <div
                className="w-[20px] sm:w-[25px] md:w-[30px] lg:w-[35px] h-[20px] sm:h-[25px] md:h-[30px] lg:h-[35px] bg-[#3e66f3] mt-16 sm:mt-20 md:mt-24 lg:mt-27 z-10 absolute"
                style={{ top: 0, left: 0 }}
              />
              <div className="flex justify-end items-center w-full ml-2 sm:ml-3 md:ml-4 lg:ml-5 relative z-0">
                <img
                  src="/img/purpose.png"
                  className="w-[200px] sm:w-[260px] md:w-[320px] lg:w-[380px]
                             h-[240px] sm:h-[320px] md:h-[380px] lg:h-[450px]
                             rounded-t-[18px] sm:rounded-t-[24px] md:rounded-t-[30px] lg:rounded-t-[36px]
                             object-cover mb-4 sm:mb-5 md:mb-6 lg:mb-6.5 z-20"
                  alt="Team working"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6 sm:gap-8 md:gap-9 lg:gap-10.5 w-full lg:w-1/2">
            {[
              {
                icon: FaEye,
                title: "Our Vision",
                desc: "To become a trusted leader in secure digital transformation, recognized for our innovation, dependability and client-first approach.",
                pr: true,
              },
              {
                icon: FaRocket,
                title: "Our Mission",
                desc: "To create meaningful change through IT innovation and strategic digital solutions, helping businesses grow sustainably, stay flexible and achieve measurable outcomes.",
                pr: false,
              },
              {
                icon: FaBullseye,
                title: "Our Purpose",
                desc: "We enable businesses to succeed through intelligent technologies, cloud solutions and innovative digital strategies. Our focus is on making every digital experience smooth, effective and impactful.",
                pr: true,
              },
              {
                icon: FaCogs,
                title: "What We Do",
                desc: "We provide end-to-end technology and digital services designed to empower businesses to grow, optimize performance and stay ahead in today’s competitive digital landscape.",
                pr: true,
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`flex justify-start items-start w-full ${
                    item.pr ? "pr-0 sm:pr-2 md:pr-2.5" : ""
                  }`}
                >
                  <div className="flex gap-3 sm:gap-4 md:gap-4.5 lg:gap-5 justify-start items-start w-full">
                    <div className="flex justify-center items-center w-[50px] sm:w-[60px] md:w-[70px] lg:w-[80px] h-[50px] sm:h-[60px] md:h-[70px] lg:h-[80px] border border-[#3e66f3] rounded-[20px] sm:rounded-[25px] md:rounded-[30px] lg:rounded-[40px] p-3 sm:p-4 md:p-4.5 lg:p-4.5 flex-shrink-0">
                      <Icon className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] text-[#3e66f3]" />
                    </div>
                    <div className="flex flex-col justify-start items-start w-full">
                      <h3 className="text-[18px] sm:text-[20px] md:text-[24px] lg:text-[28px] font-bold leading-tight text-[#210a4a]">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm md:text-sm lg:text-sm font-normal leading-5 sm:leading-5.25 text-[#5f5d5d] w-full mt-1">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Services Cards */}
        <div className="flex flex-col md:flex-row gap-4 sm:gap-5 lg:gap-6 w-full mt-8 sm:mt-10 md:mt-12 lg:mt-16 justify-center items-stretch">
          {[
            {
              icon: FaServer,
              title: "IT Solutions",
              desc: "We deliver reliable infrastructure setup, cloud support, data security, and managed IT services to keep your business operations smooth, protected and continuously efficient.",
            },
            {
              icon: FaCode,
              title: "Software Development",
              desc: "Our team builds custom web and mobile applications tailored to real business challenges, ensuring intuitive interfaces, strong performance and seamless user experiences.",
            },
            {
              icon: FaBullhorn,
              title: "Digital Marketing",
              desc: "We provide SEO, social media management, performance campaigns, and brand-building strategies that boost visibility, attract ideal audiences and grow your digital presence.",
            },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="flex justify-start items-center w-full md:w-[350px] lg:w-[380px]"
              >
                <div className="flex flex-col justify-start items-start w-full">
                  {/* Icon in front */}
                  <div className="relative z-20 flex justify-start items-center w-auto bg-[#210a4a] rounded-[19px] sm:rounded-[28px] md:rounded-[32px] lg:rounded-[38px] p-3 sm:p-3.5 md:p-4 lg:p-4.5 ml-2 sm:ml-2.5 md:ml-3 lg:ml-3">
                    <Icon className="text-[24px] sm:text-[30px] md:text-[36px] lg:text-[42px] text-white" />
                  </div>

                  {/* Card body */}
                  <div className="relative z-10 flex flex-col justify-start items-start w-full bg-white border-t-[3px] border-l-[3px] border-[#210a4a] rounded-[10px] shadow-[3px_5px_5px_rgba(0,0,0,0.1)] p-6 sm:p-7 md:p-8 lg:p-9 -mt-4 sm:-mt-5 md:-mt-6 lg:-mt-7">
                    <h3 className="text-[18px] sm:text-[22px] md:text-[26px] lg:text-[30px] font-semibold leading-tight text-black mb-2">
                      {item.title}
                    </h3>
                    <p className="text-xs sm:text-sm md:text-sm lg:text-sm font-normal leading-5 sm:leading-5.25 text-[#5f5d5d] w-full">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* IMPACTFUL NUMBERS */}
      <section className="relative py-12 sm:py-14 lg:py-20 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white overflow-hidden">
        {/* Background Lights */}
        <div className="absolute -top-28 -left-28 w-56 sm:w-72 h-56 sm:h-72 bg-blue-500/25 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 right-0 w-72 sm:w-96 h-72 sm:h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse-slow"></div>

        {/* Decorative Wave Glow */}
        <div className="absolute inset-0 opacity-10 pointer-events-none hidden sm:block">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern
              id="wave-pattern"
              x="0"
              y="0"
              width="100"
              height="100"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M0 50 Q25 0 50 50 T100 50"
                stroke="none"
                fill="currentColor"
                opacity="0.2"
              />
            </pattern>
            <rect width="100" height="100" fill="url(#wave-pattern)" />
          </svg>
        </div>

        {/* Main Wrapper */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Heading */}
          <div className="text-center mb-10 sm:mb-14">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, amount: 0.5 }}
              className="font-extrabold text-blue-200 drop-shadow-lg 
                text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight"
            >
              Impactful Numbers Reflecting Our Commitment
            </motion.h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 mt-4 max-w-2xl mx-auto leading-relaxed">
              Our numbers speak volumes about our dedication to excellence:
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 lg:gap-12">
            {stats.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: item.delay }}
                viewport={{ once: true, amount: 0.5 }}
                className="relative group bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 text-center shadow-xl border border-white/20 
                hover:shadow-blue-500/30 transition-all duration-300"
              >
                {/* Icon Animation */}
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: item.delay,
                  }}
                  className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 flex items-center justify-center 
                  bg-blue-400/10 rounded-full border border-blue-300/20"
                >
                  {item.icon}
                </motion.div>

                {/* Counter Number */}
                <h3 className="font-extrabold text-blue-100 mb-1 text-4xl sm:text-5xl md:text-6xl">
                  <CountUp end={item.number} duration={2.5} separator="," start={0} />
                  {item.suffix}
                </h3>

                <p className="text-xs sm:text-sm md:text-base font-medium text-blue-200 mt-2 px-1">
                  {item.title}
                </p>

                {/* Hover Glow Border */}
                <div className="absolute inset-0 rounded-3xl border border-blue-300/10 group-hover:border-blue-400/40 transition-all duration-500 pointer-events-none"></div>
              </motion.div>
            ))}
          </div>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true, amount: 0.5 }}
            className="text-center mt-12 sm:mt-14 text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed"
          >
            These figures underscore our global reach, industry expertise, and a track record of
            excellence in delivering IT solutions in Hyderabad and beyond.
          </motion.p>
        </div>

        {/* Keyframes */}
        <style>
          {`
          @keyframes pulse-slow {
            0%, 100% { opacity: 0.5; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.1); }
          }
          .animate-pulse-slow {
            animation: pulse-slow 10s ease-in-out infinite;
          }
          `}
        </style>
      </section>

      {/* CORE VALUES – horizontal scroll with 6 cards in one line */}
      <section className="w-full px-4 sm:px-8 md:px-14 lg:px-14 py-12 sm:py-14 md:py-16 lg:py-18 bg-white">
        <div className="flex flex-col justify-start items-center w-full">
          <h2 className="text-[20px] sm:text-[24px] md:text-[28px] lg:text-[35px] font-bold leading-tight text-[#210a4a] text-center">
            Core Values Driving Our Excellence
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-lg font-normal leading-tight text-[#5f5d5d] text-center mt-2 sm:mt-3 md:mt-4 lg:mt-4 max-w-4xl">
            Our values guide our culture, inspire innovation, strengthen
            accountability, and ensure we consistently deliver honest,
            impactful, and high-quality solutions for every client.
          </p>

          {/* HORIZONTAL SCROLLABLE ROW OF 6 CARDS */}
          <div className="relative w-full">
            {/* LEFT BUTTON */}
            <button
              onClick={() => {
                document.getElementById("valuesScroll").scrollBy({
                  left: -300,
                  behavior: "smooth",
                });
              }}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 bg-white shadow-lg w-12 h-12 rounded-full items-center justify-center text-[#3e66f3] hover:bg-[#3e66f3] hover:text-white transition z-20"
            >
              <i className="fa-solid fa-chevron-left text-xl"></i>
            </button>

            {/* SCROLL CONTAINER */}
            <div
              id="valuesScroll"
              className="w-full mt-8 sm:mt-10 md:mt-12 lg:mt-14 pb-4 overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex flex-nowrap justify-start gap-4 sm:gap-5 md:gap-6 lg:gap-7 min-w-max">
                {[
                  {
                    icon: FaShieldAlt,
                    title: "Security",
                    desc: "We design every solution with strong protection and privacy in mind, ensuring your data and systems stay safe at all times.",
                  },
                  {
                    icon: FaHandshake,
                    title: "Integrity",
                    desc: "We follow honest, transparent, and ethical practices in everything we do, building trust through our actions and results.",
                  },
                  {
                    icon: FaLightbulb,
                    title: "Innovation",
                    desc: "We use AI, automation, and cutting-edge technologies to create smarter, forward-thinking solutions that move businesses ahead.",
                  },
                  {
                    icon: FaAward,
                    title: "Excellence",
                    desc: "We focus on high quality, continuous improvement, and delivering results that consistently exceed expectations.",
                  },
                  {
                    icon: FaTasks,
                    title: "Responsibility",
                    desc: "We take ownership of our work, stand by our commitments, and ensure every solution reflects accountability and professionalism.",
                  },
                  {
                    icon: FaUsers,
                    title: "Collaboration",
                    desc: "We believe in growing together by working closely with our clients, partners, and team to achieve shared success.",
                  },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex-none w-[280px] sm:w-[300px] md:w-[320px] lg:w-[254px] bg-white border-b-[8px] border-[#3e66f3] rounded-[25px] sm:rounded-[35px] md:rounded-[42px] lg:rounded-[50px] shadow-[0px_1px_5px_rgba(0,0,0,0.1)] p-5 sm:p-6 md:p-7 lg:p-6.5"
                      data-aos="fade-up"
                      data-aos-delay={index * 100}
                    >
                      <div className="mx-auto mb-3 flex items-center justify-center">
                        <Icon className="text-[32px] sm:text-[40px] md:text-[47px] lg:text-[54px] text-[#3e66f3]" />
                      </div>
                      <h3 className="text-[16px] sm:text-[20px] md:text-[24px] lg:text-[28px] font-bold leading-tight text-[#210a4a] text-center w-full mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm sm:text-base md:text-lg lg:text-lg font-normal leading-tight text-[#5f5d5d] text-center w-full">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT BUTTON */}
            <button
              onClick={() => {
                document.getElementById("valuesScroll").scrollBy({
                  left: 300,
                  behavior: "smooth",
                });
              }}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 bg-white shadow-lg w-12 h-12 rounded-full items-center justify-center text-[#3e66f3] hover:bg-[#3e66f3] hover:text-white transition z-20"
            >
              <i className="fa-solid fa-chevron-right text-xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* WHY SECURXPERT */}
      <section className="w-full px-4 sm:px-8 md:px-14 lg:px-14 py-7 sm:py-10 md:py-12 lg:py-14 bg-white">
        <div className="flex flex-col gap-6 sm:gap-7 md:gap-8 lg:gap-8 justify-start items-center w-full">
          <h2 className="text-[20px] sm:text-[24px] md:text-[28px] lg:text-[35px] font-bold leading-tight text-[#210a4a] text-center">
            Why SecurXpert Technologies?
          </h2>

          <div className="relative w-full max-w-5xl mx-auto">
            {/* VERTICAL LINE */}
            <div
              className="hidden sm:block absolute left-1/2 -translate-x-1/2 bg-[#210a4a] rounded-full"
              style={{
                width: "3px",
                top: "27px",
                bottom: "30px",
              }}
            />

            {whyItems.map((item, index) => {
              const isLeft = index % 2 === 1;

              return (
                <div
                  key={index}
                  className="mb-12 sm:mb-16 relative"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  {/* Desktop / Tablet Zig-Zag */}
                  <div className="hidden sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                    {/* LEFT TEXT */}
                    <div className={`pr-6 ${isLeft ? "text-right" : ""}`}>
                      {isLeft && (
                        <>
                          <h3 className="text-[18px] sm:text-[20px] md:text-[22px] lg:text-[24px] font-semibold text-[#210a4a] mb-2">
                            {item.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-[#5f5d5d] leading-6">
                            {item.desc}
                          </p>
                        </>
                      )}
                    </div>

                    {/* NUMBER CIRCLE */}
                    <div className="flex items-center justify-center relative z-10">
                      <div className="w-[54px] h-[54px] rounded-full bg-[#210a4a] text-white flex items-center justify-center text-xl font-semibold shadow-md">
                        {index + 1}
                      </div>
                    </div>

                    {/* RIGHT TEXT */}
                    <div className={`pl-6 ${!isLeft ? "text-left" : ""}`}>
                      {!isLeft && (
                        <>
                          <h3 className="text-[18px] sm:text-[20px] md:text-[22px] lg:text-[24px] font-semibold text-[#210a4a] mb-2">
                            {item.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-[#5f5d5d] leading-6">
                            {item.desc}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Mobile Layout */}
                  <div className="sm:hidden flex flex-col items-center text-center gap-3">
                    <div className="w-[42px] h-[42px] rounded-full bg-[#210a4a] text-white flex items-center justify-center text-base font-semibold mb-1">
                      {index + 1}
                    </div>
                    <h3 className="text-[16px] font-semibold text-[#210a4a]">
                      {item.title}
                    </h3>
                    <p className="text-xs text-[#5f5d5d] leading-5 max-w-xs">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* JOURNEY TO DIGITAL EXCELLENCE */}
      <section className="w-full px-4 sm:px-8 md:px-14 lg:px-14 py-8 sm:py-10 md:py-12 lg:py-14 bg-white">
        <div className="flex flex-col gap-6 sm:gap-7 md:gap-8 lg:gap-8.5 justify-start items-center w-full">
          <h2 className="text-[20px] sm:text-[24px] md:text-[28px] lg:text-[35px] font-bold leading-tight text-[#210a4a] text-center">
            Join Us on the Journey to
            <br />
            Digital Excellence
          </h2>

          <div className="flex flex-col md:flex-row gap-8 sm:gap-10 md:gap-12 lg:gap-16 w-full max-w-6xl justify-center items-stretch">
            {[
              {
                icon: FaShieldAlt,
                title: "Secure Solutions",
                desc: "We protect your business with strong, reliable security practices that safeguard your data and build lasting trust.",
              },
              {
                icon: FaLightbulb,
                title: "Innovative IT Solutions",
                desc: "We use advanced technologies to create smart, forward-thinking solutions designed specifically to fit your business needs.",
              },
              {
                icon: FaHandshake,
                title: "Trusted Partnership",
                desc: "We believe in long-term relationships built on honesty, consistency, and exceptional support you can rely on every day.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex flex-col justify-start items-center w-full md:w-[320px] lg:w-[346px] bg-white border-b-[4px] border-[#3e66f3] rounded-b-[5px] shadow-[0px_-2px_4px_rgba(0,0,0,0.1)] p-6 sm:p-7 md:p-8 lg:p-8 text-center"
                  data-aos="zoom-in"
                >
                  <Icon className="text-[32px] sm:text-[40px] md:text-[47px] lg:text-[54px] text-[#3e66f3] mb-4" />
                  <h3 className="text-[16px] sm:text-[18px] md:text-[21px] lg:text-[25px] font-bold leading-tight text-[#210a4a] mb-3">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-sm lg:text-sm font-normal leading-5 sm:leading-5.25 text-[#5f5d5d] w-full">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />

      {/* SCROLL TO TOP WITH PROGRESS */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg hover:from-blue-600 hover:to-blue-800 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - scrollProgress}
              style={{ transition: "stroke-dashoffset 0.1s linear" }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default AboutUs;
